import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UserStatus {
  isApproved: boolean;
  isAdmin: boolean;
  roleLevel: 'master' | 'admin' | 'user';
  isLoading: boolean;
}

export function useUserStatus(userId: string | undefined): UserStatus & { refetch: () => void } {
  const [isApproved, setIsApproved] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [roleLevel, setRoleLevel] = useState<'master' | 'admin' | 'user'>('user');
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = () => setRefreshKey(prev => prev + 1);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const checkStatus = async () => {
      try {
        // Check if user is approved
        const { data: profile } = await supabase
          .from('profiles')
          .select('approved')
          .eq('user_id', userId)
          .single();

        setIsApproved(profile?.approved ?? false);

        // Check if user is admin
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role, role_level')
          .eq('user_id', userId);

        const hasAdminRole = roles?.some((r) => r.role === 'admin' || r.role_level === 'admin' || r.role_level === 'master') ?? false;
        const highestRole = roles?.reduce((prev, curr) => {
          if (curr.role_level === 'master') return 'master';
          if (curr.role_level === 'admin' && prev !== 'master') return 'admin';
          return prev;
        }, 'user' as 'master' | 'admin' | 'user') || 'user';

        setIsAdmin(hasAdminRole);
        setRoleLevel(highestRole);
      } catch (error) {
        console.error('Error checking user status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [userId, refreshKey]);

  return { isApproved, isAdmin, roleLevel, isLoading, refetch };
}

export async function claimAdminIfEmpty() {
  const { data, error } = await supabase.rpc('claim_admin_if_empty');
  if (error) {
    console.error('Error claiming admin rights:', error);
    return { success: false, message: error.message };
  }
  return data as { success: boolean; message: string };
}
